import LazyLoad from "vanilla-lazyload";

/**
 * Deferred image loading.
 *
 * The browser's own loading="lazy" is still the default for most images and is
 * the better mechanism, but its distance threshold is fixed at roughly 1250px
 * and cannot be configured. The images on this site are large enough that the
 * lookahead pulls several megabytes before the visitor has scrolled at all, so
 * images marked .lazy are handed to this instead, where the threshold is ours.
 *
 * threshold is deliberately not 0. Loading exactly at the viewport edge means
 * watching each image appear while scrolling; 300px is far tighter than the
 * browser's lookahead while still giving a slow connection a head start, so
 * nothing visibly pops in.
 *
 * Deferring behind data-src means the image is invisible to the browser's
 * preload scanner and will not load at all without JavaScript. That is the real
 * cost of doing this here rather than in the browser, and it is why the markup
 * carries a <noscript> copy of every deferred image.
 */
const lazyLoader = new LazyLoad({
	elements_selector: ".lazy",
	threshold: 300,
	class_loading: "is-loading",
	class_loaded: "is-loaded",
	class_error: "has-error",

	/**
	 * Reveals the product titles on the taxonomy archives.
	 *
	 * style.scss hides .product-title at opacity 0 and only shows it once the
	 * element carries .loaded:
	 *
	 *   body.tax-product_cat .filter-wrap .product-item .product-title { opacity: 0 }
	 *   ...same selector with .loaded                                  { opacity: 1 }
	 *
	 * That class used to be added by the theme's own IntersectionObserver, in a
	 * branch commented "hack to show the title of the block on image loaded".
	 * The observer was retired when these images moved to this library, and the
	 * side effect went with it -- so every stone name on /products/slab/
	 * disappeared while remaining present in the DOM.
	 *
	 * The DOM shape it walks: img -> .lazy-media-wrap -> .thumb-wrap, then the
	 * siblings of .thumb-wrap, which is where .product-title sits.
	 */
	callback_loaded: (el) => {
		el.classList.add("loaded");

		if (!el.classList.contains("lazy-title")) {
			return;
		}

		const wrap = el.parentElement && el.parentElement.parentElement;

		if (!wrap || !wrap.parentElement) {
			return;
		}

		Array.from(wrap.parentElement.children).forEach((sibling) => {
			if (sibling !== wrap) {
				sibling.classList.add("loaded");
			}
		});
	},
});

/**
 * Anything that injects markup can call this to have new images picked up.
 */
window.abodaLazyUpdate = () => lazyLoader.update();

/**
 * Late-injected images.
 *
 * Carousel slides no longer come through here -- they use the browser's own
 * loading="lazy" so Swiper can preload the ones either side -- so nothing this
 * library manages is created after the first paint on any current template.
 *
 * An always-on MutationObserver was watching document.body for them anyway, and
 * it cost real main-thread time: Swiper mutates constantly while initialising
 * seventeen carousels, and every mutation scheduled another update(). Total
 * Blocking Time doubled from 70ms to 140ms because of it.
 *
 * So the watcher now only runs until the page settles, and only reacts to added
 * nodes that actually contain a deferred image. Anything injecting images later
 * than that -- an ajax filter, say -- should call window.abodaLazyUpdate()
 * rather than have every page pay for a permanent observer.
 */
let pendingUpdate;

const domWatcher = new MutationObserver((records) => {
	const addedDeferredImage = records.some((record) =>
		[...record.addedNodes].some(
			(node) =>
				node.nodeType === 1 &&
				(node.matches?.(".lazy") || node.querySelector?.(".lazy"))
		)
	);

	if (!addedDeferredImage) {
		return;
	}

	clearTimeout(pendingUpdate);
	pendingUpdate = setTimeout(() => lazyLoader.update(), 100);
});

domWatcher.observe(document.body, { childList: true, subtree: true });

window.addEventListener("load", () => {
	lazyLoader.update();
	domWatcher.disconnect();
});
